import type { AslState, AslRetryConfig, AslCatchConfig } from '@/types/workflow-graph';

// =============================================================================
// Capability Types (per CAPABILITY_SCHEMA.md)
// =============================================================================

/**
 * All capability types as defined in the schema
 */
export type CapabilityType = 
    | 'agent' 
    | 'model' 
    | 'policy' 
    | 'prompt' 
    | 'role' 
    | 'schema' 
    | 'skill' 
    | 'subscription' 
    | 'tool' 
    | 'workflow';

/**
 * Capability semantics - what kind of operation this is
 */
export type CapabilitySemantics = 'work' | 'declarative' | 'informational';

/**
 * JSON Schema for input/output validation
 */
export interface JsonSchema {
    type: string;
    properties?: Record<string, JsonSchemaProperty>;
    required?: string[];
    description?: string;
    $schema?: string;
}

export interface JsonSchemaProperty {
    type: string;
    description?: string;
    default?: unknown;
    enum?: unknown[];
    items?: JsonSchemaProperty;
    properties?: Record<string, JsonSchemaProperty>;
    nullable?: boolean;
    minimum?: number;
    maximum?: number;
}

/**
 * Capability definition within capabilities map
 */
export interface CapabilityDefinition {
    description: string;
    semantics: CapabilitySemantics;
    input?: JsonSchema;
    inputSchema?: string;        // Reference to schema capability
    output?: JsonSchema;
    outputSchema?: string;       // Reference to schema capability
}

/**
 * Full capability metadata as defined in CAPABILITY_SCHEMA.md
 */
export interface CapabilityMetadata {
    // Base fields (all types)
    key: string;                         // Full capability key (e.g., "agent.case.fact-extractor")
    name: string;                        // Unique identifier "{type}.{domain}.{name}"
    aliases?: string[];                  // Alternative names for backward compatibility
    type: CapabilityType;
    version: string;                     // Semantic version (e.g., "1.0.0")
    description?: string;
    tags?: string[];
    capabilities?: Record<string, CapabilityDefinition>;
    
    // Derived/computed fields
    executionMode: 'sync' | 'job' | 'callback';
    inputSchema?: JsonSchema;            // Primary capability input schema
    outputSchema?: JsonSchema;           // Primary capability output schema
    
    // Agent-specific fields
    class?: string;                      // PHP class implementing the agent
    prompt?: string;                     // Prompt directory name
    model?: string;                      // Model name reference
    tools?: string[];                    // Tools available to the agent
    skills?: string[];                   // Skills to apply
    roles?: string[];                    // Roles that constrain the agent
    preProcessorWorkflow?: string;       // Input transformation workflow
    postProcessorWorkflow?: string;      // Output transformation workflow
    config?: Record<string, unknown>;    // Model parameter overrides
    
    // Model-specific fields
    provider?: string;                   // LLM provider (openai, anthropic, google)
    modelId?: string;                    // Provider-specific model ID
    parameters?: {
        timeout?: number;
        temperature?: number;
        topP?: number;
        frequencyPenalty?: number;
        presencePenalty?: number;
    };
    retry?: {
        maxAttempts?: number;
        intervalSeconds?: number;
        backoffRate?: number;
    };
    backup?: string;                     // Fallback model name
    pricing?: {
        inputPerMillion?: number;
        outputPerMillion?: number;
    };
    
    // Tool-specific fields
    // (class already covered above)
    
    // Workflow-specific fields
    adapter?: string;                    // Execution adapter
    
    // Policy-specific fields
    opaPackage?: string;                 // OPA package name
    
    // Skill-specific fields
    priority?: number;                   // Application order
    conflictsWith?: string[];            // Incompatible skills
    systemInstruction?: string;          // Text to inject into prompt
    
    // Role-specific fields
    extends?: string;                    // Parent role
    requiredSkills?: string[];
    allowedModels?: string[];
    policies?: string[];
    constraints?: {
        maxTokens?: number;
        temperatureRange?: { min: number; max: number };
        allowedTools?: string[];
        deniedTools?: string[];
        maxConcurrent?: number;
    };
    
    // Subscription-specific fields
    active?: boolean;
    source?: {
        type: 'internal' | 'webhook' | 'queue';
        identifier: string;
    };
    target?: {
        capability: string;
        method: string;
    };
    inputMapping?: Record<string, string>;
    filters?: Array<{
        path: string;
        operator: string;
        value?: unknown;
    }>;
}

// =============================================================================
// Task Configuration Types
// =============================================================================

export type TabId = 'capability' | 'input' | 'output' | 'resilience' | 'errors' | 'advanced';

export interface TaskConfig {
    // Capability binding
    capabilityKey: string;
    capabilityVersion?: string;
    
    // Input configuration
    inputPath: string;                          // Default: "$"
    parameters: Record<string, ParameterValue>; // Field mappings
    
    // Output configuration
    resultPlacement: 'replace' | 'merge' | 'discard';
    resultPath: string;                         // For merge, the key
    outputPath?: string;                        // Filter
    resultSelector?: Record<string, string>;    // Field shaping
    
    // Resilience
    timeoutSeconds?: number;
    timeoutSecondsPath?: string;                // Dynamic timeout
    heartbeatSeconds?: number;
    retry: RetryRule[];
    
    // Errors
    catch: CatchRule[];
    
    // Advanced
    comment?: string;
    customAttributes?: Record<string, string>;
}

export interface ParameterValue {
    source: 'state' | 'runtime' | 'constant';
    value: string;                              // JSONPath, variable name, or literal
}

export interface RetryRule {
    id: string;                                 // For UI keying
    errorTypes: string[];
    maxAttempts: number;
    initialDelay: number;
    backoffRate: number;
    maxDelay?: number;
    jitter: boolean;
}

export interface CatchRule {
    id: string;
    errorTypes: string[];
    targetNode: string;
    resultPath?: string;
}

// =============================================================================
// Runtime Variables
// =============================================================================

export const RUNTIME_VARIABLES = [
    { key: 'executionId', label: 'Execution ID', description: 'Unique ID for this workflow execution' },
    { key: 'workflowId', label: 'Workflow ID', description: 'ID of the workflow definition' },
    { key: 'workflowVersion', label: 'Workflow Version', description: 'Version of the workflow' },
    { key: 'traceId', label: 'Trace ID', description: 'Distributed tracing ID' },
    { key: 'currentTimestamp', label: 'Current Timestamp', description: 'ISO timestamp at execution' },
    { key: 'taskId', label: 'Task ID', description: 'ID of this specific task invocation' },
] as const;

export type RuntimeVariable = typeof RUNTIME_VARIABLES[number]['key'];

// =============================================================================
// Error Types
// =============================================================================

export const ERROR_TYPES = [
    { key: 'States.ALL', label: 'All Errors', description: 'Catches all errors' },
    { key: 'States.Timeout', label: 'Timeout', description: 'Task exceeded timeout' },
    { key: 'States.TaskFailed', label: 'Task Failed', description: 'Task returned an error' },
    { key: 'States.Permissions', label: 'Permissions', description: 'Permission denied' },
    { key: 'States.ResultPathMatchFailure', label: 'Result Path Error', description: 'Result path match failed' },
    { key: 'States.ParameterPathFailure', label: 'Parameter Path Error', description: 'Parameter path resolution failed' },
    { key: 'States.BranchFailed', label: 'Branch Failed', description: 'A parallel branch failed' },
    { key: 'States.NoChoiceMatched', label: 'No Choice Matched', description: 'No choice rule matched' },
    { key: 'States.IntrinsicFailure', label: 'Intrinsic Failure', description: 'Intrinsic function failed' },
] as const;

// =============================================================================
// Retry Presets
// =============================================================================

export const RETRY_PRESETS = {
    none: {
        label: 'None',
        description: 'No automatic retries',
        config: [],
    },
    safe: {
        label: 'Safe Default',
        description: '3 retries with exponential backoff',
        config: [{
            id: 'default',
            errorTypes: ['States.ALL'],
            maxAttempts: 3,
            initialDelay: 1,
            backoffRate: 2,
            jitter: true,
        }],
    },
    network: {
        label: 'Network Retry',
        description: '5 retries for transient network errors',
        config: [{
            id: 'network',
            errorTypes: ['States.Timeout', 'NetworkError', 'ConnectionError'],
            maxAttempts: 5,
            initialDelay: 2,
            backoffRate: 2,
            maxDelay: 30,
            jitter: true,
        }],
    },
    idempotent: {
        label: 'Idempotent Operation',
        description: 'Aggressive retry for safe-to-repeat operations',
        config: [{
            id: 'idempotent',
            errorTypes: ['States.ALL'],
            maxAttempts: 10,
            initialDelay: 1,
            backoffRate: 1.5,
            maxDelay: 60,
            jitter: true,
        }],
    },
} as const;

export type RetryPresetKey = keyof typeof RETRY_PRESETS;

// =============================================================================
// Default Configuration
// =============================================================================

export function getDefaultTaskConfig(): TaskConfig {
    return {
        capabilityKey: '',
        inputPath: '$',
        parameters: {},
        resultPlacement: 'merge',
        resultPath: '$.result',
        retry: [],
        catch: [],
    };
}

